import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import API_URL from "../../src/Config";
import "./DailyProgressEmail.css";
import Select from "react-select";

const TEMPLATE_MODULE_NAME = "Daily Progress Report";

const normalizeModuleName = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const EmailConfiguration = ({ closePopupEmail, dailyId, onClose }) => {
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isSending, setIsSending] = React.useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const popupRef = useRef(null);
  const [emailData, setEmailData] = useState({
    to_email: "",
    subject: "",
    body: "",
    dailyId: "",
  });

  const [emails, setEmails] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [moduleKey, setModuleKey] = useState("");

  useEffect(() => {
    console.log("Received dailyId in popup:", dailyId);
    // You can use dailyId here for fetching or other logic
  }, [dailyId]);

  const handleEmailChange = (selectedOption) => {
    setSelectedEmail(selectedOption); // Update the selected email in state

    // Find the selected template data from the response
    const selectedTemplate = emailTemplates.find(
      (template) => template.id === selectedOption.value
    );

    if (selectedTemplate) {
      setEmailData({
        ...emailData,
        to_email: selectedTemplate.to_emails || "", // Populate To email
        subject: selectedTemplate.subject || "", // Populate Subject
        body: selectedTemplate.body || "", // Populate Body
      });
    }
  };

  const fetchEmailsTemplate = async (moduleName) => {
    if (!moduleName) {
      console.error("Module name is required!");
      return;
    }

    try {
      const authKey = localStorage.getItem("authKey");
      const apiUrl = `${API_URL}/api/notification-config`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: authKey,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const targetName = normalizeModuleName(moduleName);
        const target = (data.records || []).find((record) => {
          const candidates = [
            record.module_key,
            record.module_label,
            record.module_name,
          ].map(normalizeModuleName);
          return candidates.some(
            (candidate) =>
              candidate === targetName ||
              candidate.includes(targetName) ||
              targetName.includes(candidate),
          );
        });

        if (!target) {
          setModuleKey("");
          return;
        }

        setModuleKey(target.module_key);
        setSelectedEmail(
          target.email_temp_id
            ? {
                label:
                  target.template_name ||
                  target.temp_name ||
                  "No Template Name",
                value: target.email_temp_id,
              }
            : null
        );
      } else {
        console.error("Failed to fetch email templates");
      }
    } catch (error) {
      console.error("An error occurred while fetching email templates:", error);
    }
  };
  // Use the encoded module name
  useEffect(() => {
    fetchEmailsTemplate(TEMPLATE_MODULE_NAME);
  }, []);

  const fetchEmails = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const apiUrl = `${API_URL}/api/get_email_templates`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: authKey,
        },
      });

      if (response.ok) {
        const data = await response.json();

        setEmailTemplates(data.templates); // Store the raw templates

        const options = data.templates.map((email) => {
          const label = email.temp_name || "No Template Name"; // Use temp_name or fallback
          return {
            label: label,
            value: email.id,
          };
        });

        setEmails(options); // Update state with formatted options
      } else {
        console.error("Failed to fetch email templates");
      }
    } catch (error) {
      console.error("An error occurred while fetching email templates:", error);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!selectedEmail) {
      errors.from_email = "Email is required";
    }
    if (!emailData.to_email) {
      errors.to_email = "Email is required";
    }
    if (!emailData.subject) {
      errors.subject = "Subject is required";
    }
    if (!emailData.body) {
      errors.body = "Body is required";
    }

    // Return true if no errors, false otherwise
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      if (!moduleKey) {
        setErrorMessage("Daily Progress Report template module was not found.");
        return;
      }
      if (!selectedEmail?.value) {
        setErrorMessage("Please select an email template.");
        return;
      }

      const authKey = localStorage.getItem("authKey");
      const payload = {
        email_temp_id: selectedEmail.value,
      };

      const response = await axios.put(
        `${API_URL}/api/notification-config/${moduleKey}`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSuccessMessage(response.data.message); // Show dynamic success message
        setShowSuccessMessage(true);
      } else {
        setErrorMessage(`Error: ${response.data.message}`);
      }
      onClose();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setErrorMessage(error.response.data.message); // Display error message from API
      } else {
        setErrorMessage("Failed to send email. Please try again."); // General fallback error
      }
      console.error("Error sending email:", error);
    } finally {
      setIsSending(false); // Hide "Sending email..." message
    }
  };

  return (
    <div className="reminder-popup-model" ref={popupRef}>
      <div className="reminder-popup-content">
        <div className="reminder-popup-model-header">
          <div className="reminder-popup-model-content-header">
            <div className="reminder-popup-model-content-header-left-part">
              Email Template Config
            </div>
            <div className="reminder-popup-model-content-header-right-part"></div>
          </div>
          <div className="popup-new-form">
            <form className="popup-form-detail">
              <div className="new-data-form">
                <div className="form-group-select email-filed">
                  <label htmlFor="department_name">
                    Select Template<span className="text-danger">*</span>
                  </label>
                  <Select
                    id="department_name"
                    options={emails} // emails should be in the format { label, value }
                    value={selectedEmail}
                    onChange={handleEmailChange}
                    placeholder="Select email template"
                    isSearchable
                  />
                </div>
                <div className="form-group email-filed">
                  <label htmlFor="to_email">To</label>
                  <div className="input-group emailconfig1">
                    <input
                      type="email"
                      id="to_email"
                      name="to_email"
                      value={emailData.to_email}
                      disabled
                    />
                  </div>
                </div>
                <div className="form-group email-filed">
                  <label htmlFor="subject">Subject</label>
                  <div className="input-group subject1">
                    <textarea
                      id="subject"
                      name="subject"
                      value={emailData.subject}
                      disabled
                    />
                  </div>
                </div>
                <div className="form-group email-filed">
                  <label htmlFor="body">Body</label>
                  <div className="input-group body1">
                    <textarea
                      id="body"
                      name="body"
                      value={emailData.body}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="footers">
            <div className="button-group-email">
              <div>
                <button
                  className="person-user-save-button"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSending}
                >
                  {isSending ? "Saving..." : "Save"}
                </button>

                <button className="person-user-cancel-button" onClick={onClose}>
                  Cancel
                </button>
              </div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfiguration;
